import React, { ReactNode, useEffect, useState } from "react"
import {
    Filters,
    IItem as PFIItem,
    ItemRelation as ItemRelationPF,
    Options,
    SortingOption,
} from "@tenforce/product-fields"

import { Symbols } from "@tenforce/toolbox-fontmap"
import { Action, ActionAnchor } from "@tenforce/toolbox-button-push-react"

import { IItemRelationField } from "../.."
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"
import { getItemDetailUrl } from "../../../../util/UrlGenerator"
import env from "../../../../env"
import {
    fetchAvailableSortingFields,
    fetchCollectionItems as fetchCollectionItemsThunk,
    fetchCollectionSelectedItems as fetchCollectionSelectedItemsThunk,
    fetchCollectionDirtySelectedItems as fetchCollectionDirtySelectedItemsThunk,
    fetchConceptFields,
    getCollectionQuery,
    updateCollectionQuery,
} from "../../../collection/slice"
import { FetchCollectionParams } from "../../../collection"
import { Field } from "../Field"
import { IItem } from "../../../../models"
import { formatString } from "../../../../util/Strings"
import { updateField } from "../../slice"
import { useHierarchicalSelectableData } from "../../../../hooks/useHierarchicalSelectableData"
import {
    FieldDefinitionTypeToSelectableDTOTypeMap,
    getSelectableConditionType,
} from "../../../../apis/utils/conditions/SelectorConditionParser"
import { SelectableValue } from "../../../../apis/treeSelector/types"
import { ITenForceWindow } from "../../../../global"

type Props = IItemRelationField

declare let window: ITenForceWindow

// TODO: These are just to trick tailwind into generating the dynamically created classes from the product-fields.
// It should at some point be removed once the SCSS is generated properly.
const _levelOne = "tw-ml-0"
const _levelTwo = "tw-ml-4"
const _levelThree = "tw-ml-8"
const _levelFour = "tw-ml-12"
const _levelFive = "tw-ml-16"

type SortingLevel = 0 | 1 | 2 | null

const PAGE_SIZE = 20
const MAX_SELECTED_PAGE_SIZE = 1000

const convertNewUIItemToPFItem = (item: IItem): PFIItem => {
    return {
        id: item.id,
        number: item.number ?? "",
        title: item.title ?? "",
        url: getItemDetailUrl(env.applicationPath, "", item.id),
        parents: item.parents,
        fields: Object.values(item.fields ?? {}),
    }
}

const ItemRelation: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [isSortingLoading, setIsSortingLoading] = useState<boolean>(false)
    const [isFieldSelectionLoading, setIsFieldSelectionLoading] =
        useState<boolean>(true)

    const {
        description,
        fieldId,
        isDisabled,
        isLoading = false,
        isSingleSelect,
        itemId,
        itemType,
        odataType,
        title,
        value: initialValue,
        ...rest
    } = props

    const { isLoading: isLoadingGlobal, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const {
        availableSortingOptions,
        dirtySelectedItems = [],
        filters,
        isQueryProcessing,
        items = [],
        selectedItems = [],
        selectableFieldsData,
        selectedSorting = [],
        total,
        visibleFields = [],
        visibleFieldsIds,
    } = useAppSelector((state: GlobalState) => state.collections[fieldId] || {})
    const [localSelectedSorting, setLocalSelectedSorting] =
        useState<SortingOption[]>(selectedSorting)
    const [activeSorting, setActiveSorting] = useState<SortingLevel>(null)

    // Relation filter ====================

    const { fetchOptions, fetchSelectedOptions } =
        useHierarchicalSelectableData({ pageSize: PAGE_SIZE, itemId, fieldId })

    // =====================================

    useEffect(() => {
        fetchCollectionSelectedItems(
            0,
            MAX_SELECTED_PAGE_SIZE,
            initialValue.map((item) => item.id),
        )
    }, [initialValue])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = async (): Promise<void> => {
        await dispatch(
            getCollectionQuery({
                itemId,
                fieldId,
                odataType,
            }),
        )
    }

    const fetchCollectionItems = async (
        skip: number,
        limit: number,
        ids: number[] | null,
    ): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: skip,
                top: limit,
                ids,
            }),
        )
    }

    const fetchCollectionSelectedItems = async (
        skip: number,
        limit: number,
        ids: number[] | null,
    ): Promise<void> => {
        await dispatch(
            fetchCollectionSelectedItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: skip,
                top: limit,
                ids,
            }),
        )
    }

    const fetchCollectionDirtySelectedItems = async (
        skip: number,
        limit: number,
        ids: number[] | null,
    ): Promise<void> => {
        await dispatch(
            fetchCollectionDirtySelectedItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: skip,
                top: limit,
                ids,
            }),
        )
    }

    const skipValueIds = (activeLevel: SortingLevel): string[] => {
        if (activeLevel === 1) {
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                return localSelectedSorting[0].columnId
                    ? [localSelectedSorting[0].columnId]
                    : []
            }
            return []
        }

        if (activeLevel === 2) {
            const ids: string[] = []
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                if (localSelectedSorting[0].columnId) {
                    ids.push(localSelectedSorting[0].columnId)
                }
                if (localSelectedSorting[1]?.columnId) {
                    ids.push(localSelectedSorting[1].columnId)
                }
            }
            return ids
        }

        return []
    }

    const fetchSortingFields = async (
        activeLevel: SortingLevel,
    ): Promise<void> => {
        setIsSortingLoading(true)
        setActiveSorting(activeLevel)
        const localSelectedId = skipValueIds(activeLevel)

        await dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                skip: 0,
                top: PAGE_SIZE,
                localSelectedSortingId: localSelectedId,
            }),
        )
        setIsSortingLoading(false)
    }

    const fetchSelectableFields = async (): Promise<void> => {
        setIsFieldSelectionLoading(true)
        await dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                top: PAGE_SIZE,
                skip: 0,
            }),
        )
        setIsFieldSelectionLoading(false)
    }

    const onSaveViewSettings = async (settingsData: {
        selectedFields?: string[]
        sorting?: SortingOption[]
    }): Promise<void> => {
        const apiPayload: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            selectedFields: settingsData.selectedFields
                ? settingsData.selectedFields
                : visibleFieldsIds,
            sorting:
                settingsData?.sorting?.map(
                    (option: SortingOption): SortingOption => {
                        return {
                            mode: option.mode,
                            columnId: option.columnId,
                            label: option.label,
                        }
                    },
                ) ?? [],
        }

        await dispatch(updateCollectionQuery(apiPayload)).unwrap()
        if (settingsData.sorting) {
            setLocalSelectedSorting(settingsData.sorting)
        }
        setActiveSorting(0)
        await fetchCollectionData()
        await fetchCollectionItems(0, PAGE_SIZE, null)
        await fetchCollectionSelectedItems(
            0,
            MAX_SELECTED_PAGE_SIZE,
            initialValue.map((item) => item.id),
        )
    }

    const onFilterChange = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: PAGE_SIZE,
                skip: 0,
            }),
        )
    }

    const onLoadMoreSelectableFields = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: PAGE_SIZE,
                skip: selectableFieldsData.currentSkip,
            }),
        )
    }

    const onSave = async (itemIds: number[]): Promise<void> => {
        await dispatch(
            updateField({
                field: props,
                newValue: itemIds,
            }),
        ).unwrap()
    }

    const onSortingFilterChange = async (filter: string): Promise<void> => {
        setIsSortingLoading(true)
        await dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: 0,
                top: PAGE_SIZE,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        ).unwrap()
        setIsSortingLoading(false)
    }

    const onSortingLoadMore = (filter: string): void => {
        setIsSortingLoading(true)
        const nextSkip = availableSortingOptions?.currentSkip || 0
        dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: nextSkip,
                top: PAGE_SIZE,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        )
        setIsSortingLoading(false)
    }

    const onCollectionFiltersChange = async (
        filters: Filters,
    ): Promise<void> => {
        const args: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            filters,
        }

        await dispatch(updateCollectionQuery(args)).unwrap()
        await fetchCollectionData()
        await fetchCollectionItems(0, PAGE_SIZE, null)
        await fetchCollectionSelectedItems(
            0,
            MAX_SELECTED_PAGE_SIZE,
            initialValue.map((item) => item.id),
        )
    }

    const {
        ClickHereToEdit,
        Columns,
        FilterAllNValues,
        FilterNSelectedValues,
        FilterOnX,
        ItemOverviewSorting,
        ItemPickerAddItem,
        ItemPickerAddItems,
        ItemPickerEditItem,
        ItemPickerEditItems,
        ItemboxFilter,
        LoadNMore,
        LoadNMoreOf,
        NothingFound,
        OpenViewSettings,
        ShowAllValues,
        ShowSelectedValues,
        SortLevelOne,
        SortLevelThree,
        SortLevelTwo,
        ViewSettings,
        EmptyValue,
        RemoveEmptyValue,
    } = Labels

    const regionalDecimalSeparator = useAppSelector(
        (state: GlobalState) =>
            state.userFormattingSettings.numberFormat.decimalSeparator,
    )

    // Relation filter ======================

    const fetchSelectableFilterOptions = async (
        filter: string,
        activeFieldId: string,
        type: string,
        skip: number,
    ): Promise<Options<SelectableValue>> => {
        const oDataType =
            type as keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap
        const selectableApiType = getSelectableConditionType(oDataType)
        return await fetchOptions(
            filter,
            activeFieldId,
            selectableApiType,
            skip,
            [],
        )
    }

    const fetchSelectedFilterOptions = async (
        filterValue: string,
        selectedIdsParam: string[],
        _fieldId: string,
        type: string,
    ): Promise<
        Omit<Options<SelectableValue>, "resultCount" | "isGroupAsValue">
    > => {
        const oDataType =
            type as keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap
        const selectableApiType = getSelectableConditionType(oDataType)

        return await fetchSelectedOptions(
            filterValue,
            selectedIdsParam,
            selectableApiType,
        )
    }
    // =====================================

    return (
        <ItemRelationPF
            {...rest}
            fieldId={fieldId}
            title={title}
            isDisabled={isFieldDisabled}
            isLoading={isLoadingGlobal || isLoading || isQueryProcessing}
            isSingleSelect={isSingleSelect}
            value={selectedItems.map(convertNewUIItemToPFItem)}
            labels={{
                AddItem: ItemPickerAddItem,
                AddItems: ItemPickerAddItems,
                EditItem: ItemPickerEditItem,
                EditItems: ItemPickerEditItems,
                EmptyPlaceholder: ClickHereToEdit,
                FieldSelectionSectionLabel: Columns,
                FilterAllNValues: (count) =>
                    Symbols.Search + formatString(FilterAllNValues, count),
                FilterOnX: (fieldName) =>
                    formatString(FilterOnX, fieldName.toLowerCase()),
                FilterNSelectedValues: (count) =>
                    Symbols.Search + formatString(FilterNSelectedValues, count),
                FilterNoResults: NothingFound,
                LoadNMore: (remaining) => formatString(LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(LoadNMoreOf, pageSize, remaining),
                OpenViewSettings: OpenViewSettings,
                ShowAllValuesTooltip: ShowAllValues,
                ShowSelectedValuesTooltip: ShowSelectedValues,
                SortLevelOne: SortLevelOne,
                SortLevelThree: SortLevelThree,
                SortLevelTwo: SortLevelTwo,
                SortingSectionLabel: ItemOverviewSorting,
                ToggleFilter: ItemboxFilter,
                ViewSettings: ViewSettings,
                EmptyValue: EmptyValue,
                RemoveEmptyValue: RemoveEmptyValue,
            }}
            onSave={onSave}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            collection={{
                fetchCollectionData: fetchCollectionData,
                getItemField: (itemField): JSX.Element => {
                    return (
                        <Field
                            field={{ ...itemField, itemType }}
                            isLoading={false}
                        />
                    )
                },
                onSaveViewSettings: onSaveViewSettings,
                visibleFields: visibleFields,
                fieldSelection: {
                    isLoading: isFieldSelectionLoading,
                    onFilterChange: onFilterChange,
                    onLoadMore: onLoadMoreSelectableFields,
                    selectableFields: {
                        data: selectableFieldsData?.allConceptFields || [],
                        totalCount: selectableFieldsData?.totalValueCount || 0,
                        currentSkip: selectableFieldsData?.currentSkip || 0,
                        hasMore: selectableFieldsData?.hasMore || false,
                    },
                    fetchOptions: fetchSelectableFields,
                },
                sorting: {
                    isLoading: isSortingLoading,
                    fetchSelectable: fetchSortingFields,
                    selected: selectedSorting.map((sorting) => {
                        return {
                            columnId: sorting.columnId,
                            mode: sorting.mode,
                            label: sorting.label,
                        }
                    }),
                    localSelected: localSelectedSorting,
                    selectableOptions: {
                        data: availableSortingOptions?.selectableOptions || [],
                        hasMore: availableSortingOptions?.hasMore || false,
                        totalCount:
                            availableSortingOptions?.totalValueCount || 0,
                        currentSkip: availableSortingOptions?.currentSkip || 0,
                    },
                    onFilterChange: onSortingFilterChange,
                    onLoadMore: onSortingLoadMore,
                    onChange: (sorting: SortingOption[]): void =>
                        setLocalSelectedSorting(sorting),
                },
                filtering: {
                    filters: filters,
                    regionalDecimalSeparator: regionalDecimalSeparator,
                    onSaveFilters: onCollectionFiltersChange,
                    filterSelectorProps: {
                        fetchSelectableOptions: fetchSelectableFilterOptions,
                        fetchSelectedOptions: fetchSelectedFilterOptions,
                    },
                },
                renderRowActions: (item): ReactNode => {
                    return (
                        <div className="is-hidden-action">
                            <ActionAnchor
                                href={getItemDetailUrl(
                                    env.applicationPath,
                                    "",
                                    item.id,
                                )}
                            >
                                {Symbols.ArrowForward}
                            </ActionAnchor>
                            <Action
                                id={`context-${fieldId}-${item.id}`}
                                onClick={(): void => {
                                    window.newUI.itemBoxContextMenu.show(
                                        {
                                            id: item.id,
                                            number: item.number,
                                            title: item.title,
                                            isEditable: true, // Not sure where to get this info?
                                        },
                                        document.getElementById(
                                            `context-${fieldId}-${item.id}`,
                                        ),
                                        true,
                                    )
                                }}
                            >
                                {Symbols.EllipsisVertical}
                            </Action>
                        </div>
                    )
                },
                selectableView: {
                    options: items.map(convertNewUIItemToPFItem),
                    totalCount: total,
                    fetchOptions: async (
                        _filter,
                        skip,
                        limit,
                    ): Promise<void> => {
                        return fetchCollectionItems(skip, limit, null)
                    },
                },
                selectedView: {
                    options: dirtySelectedItems.map(convertNewUIItemToPFItem),
                    totalCount: dirtySelectedItems.length,
                    fetchOptions: async (_filter, ids): Promise<void> => {
                        return fetchCollectionDirtySelectedItems(
                            0,
                            MAX_SELECTED_PAGE_SIZE,
                            ids.map(Number.parseInt),
                        )
                    },
                },
            }}
        />
    )
}

export default ItemRelation
